import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useLocation, useNavigate } from "react-router";
import Header from "../../../components/wizard/Header";
import Footer from "../../../components/wizard/Footer/Footer";
import EditableDateRangeFilter from "../../../views/favoriteReport/EditableDateRangeFilter";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import { useBanner } from "../../../contexts/banner";
import { EXPORT_BILLING_LINE_ITEMS_EXCEL_MUTATION } from "../../../constants/graphql/mutations";

const UI_DATE_FORMAT = "MM/DD/YYYY";
const MODAL_TITLE = "Itemized Billing Excel Export";

export const BILLING_LINE_ITEMS_EXPORT_PARAMS = {
  open: "billingLineItemsExport",
  step: "billingLineItemsExportStep",
  mode: "billingLineItemsExportMode",
  startDate: "billingLineItemsExportStartDate",
  endDate: "billingLineItemsExportEndDate",
};

const toIsoDate = value => {
  if (!value) return null;
  const parsed = moment(value, UI_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const toUiDate = value => {
  if (!value) return null;
  const parsed = moment(value, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format(UI_DATE_FORMAT) : null;
};

const stepFromParams = params => {
  const step = parseInt(params.get(BILLING_LINE_ITEMS_EXPORT_PARAMS.step), 10);
  return [0, 1].includes(step) ? step : 0;
};

const allTimeFromParams = params => params.get(BILLING_LINE_ITEMS_EXPORT_PARAMS.mode) === "all";

export default function BillingLineItemsExportModal({ projectId, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialParams = new URLSearchParams(location.search);
  const [step, setStep] = useState(() => stepFromParams(initialParams));
  const [allTime, setAllTime] = useState(() => allTimeFromParams(initialParams));
  const [startDate, setStartDate] = useState(() =>
    toUiDate(initialParams.get(BILLING_LINE_ITEMS_EXPORT_PARAMS.startDate))
  );
  const [endDate, setEndDate] = useState(() =>
    toUiDate(initialParams.get(BILLING_LINE_ITEMS_EXPORT_PARAMS.endDate))
  );
  const [validationError, setValidationError] = useState("");
  const { notifyError } = useNotification();
  const { displayBanner } = useBanner();
  const [exportBillingLineItems, { loading }] = useMutation(
    EXPORT_BILLING_LINE_ITEMS_EXCEL_MUTATION
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setStep(stepFromParams(params));
    setAllTime(allTimeFromParams(params));
    setStartDate(toUiDate(params.get(BILLING_LINE_ITEMS_EXPORT_PARAMS.startDate)));
    setEndDate(toUiDate(params.get(BILLING_LINE_ITEMS_EXPORT_PARAMS.endDate)));
  }, [location.search]);

  const updateUrlParams = useCallback(
    (updates = {}, removals = []) => {
      const params = new URLSearchParams(location.search);
      params.set(BILLING_LINE_ITEMS_EXPORT_PARAMS.open, "true");

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });

      removals.forEach(key => params.delete(key));
      navigate({ search: params.toString() }, { replace: true });
    },
    [location.search, navigate]
  );

  const setStepParam = useCallback(
    nextStep => {
      setStep(nextStep);
      updateUrlParams({ [BILLING_LINE_ITEMS_EXPORT_PARAMS.step]: String(nextStep) });
    },
    [updateUrlParams]
  );

  const setModeParam = useCallback(
    nextAllTime => {
      setAllTime(nextAllTime);
      setValidationError("");
      updateUrlParams(
        { [BILLING_LINE_ITEMS_EXPORT_PARAMS.mode]: nextAllTime ? "all" : "range" },
        nextAllTime
          ? [
              BILLING_LINE_ITEMS_EXPORT_PARAMS.startDate,
              BILLING_LINE_ITEMS_EXPORT_PARAMS.endDate,
            ]
          : []
      );
    },
    [updateUrlParams]
  );

  const setStartDateParam = useCallback(
    value => {
      setStartDate(value);
      setValidationError("");
      updateUrlParams({
        [BILLING_LINE_ITEMS_EXPORT_PARAMS.mode]: "range",
        [BILLING_LINE_ITEMS_EXPORT_PARAMS.startDate]: toIsoDate(value),
      });
    },
    [updateUrlParams]
  );

  const setEndDateParam = useCallback(
    value => {
      setEndDate(value);
      setValidationError("");
      updateUrlParams({
        [BILLING_LINE_ITEMS_EXPORT_PARAMS.mode]: "range",
        [BILLING_LINE_ITEMS_EXPORT_PARAMS.endDate]: toIsoDate(value),
      });
    },
    [updateUrlParams]
  );

  const handleExport = useCallback(() => {
    if (!allTime) {
      if (!startDate || !endDate) {
        setValidationError("Select a start and end date, or switch on All Time.");
        return;
      }

      const start = moment(startDate, UI_DATE_FORMAT, true);
      const end = moment(endDate, UI_DATE_FORMAT, true);
      if (start.isValid() && end.isValid() && start.isAfter(end)) {
        setValidationError("Start date must be on or before end date.");
        return;
      }
    }

    setValidationError("");
    displayBanner({ message: "Generating file...", type: "export-excel" });
    exportBillingLineItems({
      variables: {
        projectId,
        startDate: allTime ? null : toIsoDate(startDate),
        endDate: allTime ? null : toIsoDate(endDate),
      },
      onSuccess: () => {
        onClose();
      },
      onFailure: error => {
        notifyError(error?.submit || "Unable to start billing export.");
      },
    });
  }, [
    allTime,
    endDate,
    exportBillingLineItems,
    displayBanner,
    notifyError,
    onClose,
    projectId,
    startDate,
  ]);

  const renderIntro = () => (
    <Box textAlign="center">
      <Box mb={3} display="flex" justifyContent="center">
        <img
          src="/itemized-billing-export.png"
          alt={MODAL_TITLE}
          style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8 }}
        />
      </Box>
      <Typography variant="body1" align="center" fontSize={17} sx={{ maxWidth: 520, mx: "auto" }}>
        Generate an itemized Excel export with inbound, outbound, and internal billing line
        items for the selected time period.
      </Typography>
    </Box>
  );

  const renderDateSelection = () => (
    <Box pt={2}>
      <Typography variant="body2" color="text.secondary" align="center" mb={4}>
        Choose the time period for the billing line-item export.
      </Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={allTime ? "all" : "range"}
        onChange={(event, value) => {
          if (!value) return;
          setModeParam(value === "all");
        }}
        sx={{ mb: 4, width: "82%", mx: "auto", display: "flex" }}
      >
        <ToggleButton value="range" sx={{ flex: 1 }}>
          Date Range
        </ToggleButton>
        <ToggleButton value="all" sx={{ flex: 1 }}>
          All Time
        </ToggleButton>
      </ToggleButtonGroup>

      {!allTime && (
        <EditableDateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDateParam}
          onEndDateChange={setEndDateParam}
        />
      )}

      {validationError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {validationError}
        </Alert>
      )}
    </Box>
  );

  const renderActions = () => {
    if (step === 0) {
      return (
        <Button variant="contained" color="primary" onClick={() => setStepParam(1)}>
          Start
        </Button>
      );
    }

    return (
      <Box display="flex" gap={1}>
        <Button color="inherit" onClick={() => setStepParam(0)}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExport}
          disabled={loading}
          startIcon={loading ? <CircularProgress color="inherit" size={18} /> : null}
          sx={{ minWidth: 150 }}
        >
          Generate
        </Button>
      </Box>
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      sx={{ "& .MuiDialog-paper": { maxWidth: 560, width: "100%" } }}
    >
      <Box display="flex" flexDirection="column" minHeight={460}>
        <Header title={MODAL_TITLE} onClose={onClose} />
        <DialogContent sx={{ px: 3, py: 2, flexGrow: 1 }}>
          {step === 0 && renderIntro()}
          {step === 1 && renderDateSelection()}
        </DialogContent>
        <Footer step={step} steps={2} showRaceMode={false}>
          {renderActions()}
        </Footer>
      </Box>
    </Dialog>
  );
}
